import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Redirect, Route, Switch} from 'react-router';
import PropTypes from 'prop-types';
import lodash from 'lodash';

import LoadingComponent from '../../../../components-new/ui/LoadingComponent';
import locales from 'locales';
import projectConfig from 'config';
import {addNotification as addNotificationAction} from '../../../../services/notification/action';

import './styles/index.css';
import GraphEditor from './containers/GraphEditor';
import {
    addNodeForButton as addNodeForButtonApi,
    connectEdgeToDestination as connectEdgeToDestinationApi,
    deleteNode as deleteNodeApi,
    editNode as editNodeApi,
    fetchGraph as fetchGraphApi,
    getCompany,
    removeButtonConnection as removeButtonConnectionApi,
} from './services/api';
import {t} from "../../../../components/Translate";

const notificationOptParams = {position: 'br'};

const localizeNodesData = (nodes, locale) =>
    nodes.map((node) => {
        const copy = lodash.cloneDeep(node);
        copy.name = copy.name[locale] || '';
        copy.message = copy.message[locale] || '';
        copy.tags = copy.tags[locale] || [];
        // nodes that predate this field have no map at all
        copy.outOfHoursText = (copy.outOfHoursText || {})[locale] || '';
        copy.buttons.forEach((button) => {
            button.text = button.text[locale] || '';
        });
        return copy;
    });

const getNodeUpdatedDataWithLocalization = (node, localizedNode, locale) => {
    const copy = lodash.cloneDeep(node);
    copy.name[locale] = localizedNode.name;
    copy.message[locale] = localizedNode.message;
    copy.tags[locale] = localizedNode.tags;
    copy.outOfHoursText = {...(node.outOfHoursText || {}), [locale]: localizedNode.outOfHoursText};

    copy.buttons = [];
    localizedNode.buttons.forEach((button) => {
        let editedButton;
        if (button.id >= 0) {
            editedButton = node.buttons.find(curButton => curButton.id === button.id);
            editedButton.text[locale] = button.text;
        } else {
            editedButton = lodash.cloneDeep(button);
            editedButton.text = {[locale]: button.text};
        }
        copy.buttons.push(editedButton);
    });

    copy.terminalMessage = localizedNode.terminalMessage;
    copy.chatWithOperator = localizedNode.chatWithOperator;
    copy.callToOperator = localizedNode.callToOperator;
    copy.toStart = localizedNode.toStart;
    copy.nodeType = localizedNode.nodeType;
    /*
     * Always an array, never absent.
     *
     * The graph omits botAttachments entirely when a node has none, so a caller
     * that passes the node back as it received it - the delete-button item in
     * the context menu does exactly that - leaves the field undefined, and
     * JSON.stringify drops it on the way out. Defaulting here covers every
     * caller, because this function is the one door to editNode.
     */
    copy.botAttachments = localizedNode.botAttachments || [];

    return copy;
};

const Graph = ({user, addNotification, match, location, tariff, company}) => {
    const [clientId, setClientId] = useState(-1);
    const [activeTreeId, setActiveTreeId] = useState(-1);
    const [draftTreeId, setDraftTreeId] = useState(-1);
    const [rootNodeId, setRootNodeId] = useState(-1);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [locale, setLocale] = useState(projectConfig.defaultLocaleOption.value);
    const [localizedNodes, setLocalizedNodes] = useState([]);
    const [selectedLocaleOption, setSelectedLocaleOption] = useState(projectConfig.defaultLocaleOption);
    const [isFetching, setIsFetching] = useState(false);

    const fetchGraph = useCallback((activeTreeId) => {
        if (activeTreeId === -1) return
``
        setIsFetching(true);
        return fetchGraphApi(activeTreeId).then((response) => {
            const {nodes, rootId, edges} = response;
            nodes.forEach((node) => {
                if (node.buttons === null) node.buttons = [];
            });
            setRootNodeId(rootId);
            setNodes(nodes);
            setEdges(edges);
            setLocalizedNodes(localizeNodesData(nodes, locale));
        }).finally(() => setIsFetching(false));
    }, [fetchGraphApi]);

    useEffect(() => {
        const {clientId} = match.params;
        setClientId(clientId);
        setLoading(true);

        getCompany(clientId)
            .then((res) => {
                const {activeTreeId, draftTreeId} = res;
                setActiveTreeId(activeTreeId);
                setDraftTreeId(draftTreeId);

                return fetchGraph(activeTreeId)
            })
            .catch(() => {
                addNotification(`${locales[user.language].somethingWentWrong}`, projectConfig.notificationLevels.error);
            })
            .finally(() => setLoading(false));
    }, [match.params.clientId, user.language, locale]);

    const handleLocaleChange = useCallback((option) => {
        if (option.value !== selectedLocaleOption.value) {
            setSelectedLocaleOption(option);
            setLocale(option.value);
            setLocalizedNodes(localizeNodesData(nodes, option.value));
        }
    }, [nodes, selectedLocaleOption]);

    const addNode = useCallback((buttonId) => {
        if (edges.some(edge => edge.buttonId === buttonId) || isFetching) return

        const tempNode = {
            name: {[locale]: t({user, text: 'newNode'})},
            message: {[locale]: t({user, text: 'someText'})},
        };
        setIsFetching(true);
        return addNodeForButtonApi(buttonId, activeTreeId, tempNode)
            .then((response) => {
                if(response.error) return
                const newNode = response.data
                const newNodes = [...nodes, newNode];
                const newEdges = [...edges, {buttonId, questionId: newNode.id}];
                setNodes(newNodes);
                setEdges(newEdges);
                setLocalizedNodes(localizeNodesData(newNodes, locale));
            })
            .catch(() => {
                addNotification(`${locales[user.language].canNotAddNode}`, projectConfig.notificationLevels.error);
            })
            .finally(() => setIsFetching(false));
    }, [edges, nodes, locale, addNotification, user.language, isFetching]);

    const deleteNode = useCallback((node) => {
        if (isFetching) return
        setIsFetching(true);
        return deleteNodeApi(node.id,activeTreeId).then((response) => {
            if(response.error) return
            addNotification("deleted", projectConfig.notificationLevels.info);
            return fetchGraph(activeTreeId)
        }).catch(e => {
            console.error(e);
            setIsFetching(false);
        });
    }, [nodes, edges, locale, isFetching]);

    const connectButtonToDestination = useCallback((buttonId, nodeId) => {
        if (isFetching) return
        setIsFetching(true);
        connectEdgeToDestinationApi(buttonId, nodeId, activeTreeId).then((response) => {
            if(response.error) return
            const newEdge = response.data
            const filteredEdges = edges.filter(edge => edge.buttonId !== buttonId);
            setEdges([...filteredEdges, newEdge]);
        }).finally(
            () => setIsFetching(false),
        );
    }, [edges, isFetching]);

    const editNode = useCallback((localizedNode) => {
        const node = nodes.find(curNode => curNode.id === localizedNode.id);
        const updatedNode = getNodeUpdatedDataWithLocalization(node, localizedNode, locale);
        if (isFetching) return;
        setIsFetching(true);

        return editNodeApi(updatedNode,activeTreeId).then((response) => {
            addNotification("edited", projectConfig.notificationLevels.info);
            return fetchGraph(activeTreeId)
        }).catch(e => {
            setIsFetching(false);

            const response = e.response.data

            if (!response.error) throw e;

            if (response.error.message) {
                addNotification("error.messageTextTooLong", projectConfig.notificationLevels.error);
            }

            if (response.error.button) {
                addNotification("error.buttonTextTooLong", projectConfig.notificationLevels.error);
            }

            throw e // needs to re-throw so that edit node panel doesn't close
        });
    }, [nodes, edges, locale, isFetching]);

    const removeButtonConnection = useCallback((buttonId) => {
        if (!buttonId || isFetching) return;
        setIsFetching(true);
        removeButtonConnectionApi(buttonId,activeTreeId).then((res) => {
            if (!res.error && res.data) {
                setEdges(edges.filter(edge => edge.buttonId !== buttonId));
            }
        }).finally(() => setIsFetching(false));
    }, [edges, isFetching]);

    if (loading) return <LoadingComponent/>;

    const {url: matchUrl} = match;

    const graphEditorProps = {
        nodes: localizedNodes,
        rootNodeId,
        edges,
        deleteNode,
        editNode,
        addNode,
        cleanTemporaryNodes: addNode,
        addNotification,
        connectButtonToDestination,
        removeButtonConnection,
        user,
        company,
        tariff,
        isFetching
    };

    return (
        <div className="graph-wrapper">
            <Switch>
                <Route
                    path={`${matchUrl}/editor`}
                    render={(props) => (
                        <GraphEditor {...props} {...graphEditorProps} />
                    )}
                />
                <Route
                    path={`${matchUrl}/`}
                    render={() => <Redirect to={`${matchUrl}/editor`}/>}
                />
            </Switch>
        </div>
    );
}

Graph.propTypes = {
    user: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    addNotification: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    tariff: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    user: state.user,
    company: state.company,
});

const mapDispatchToProps = (dispatch) => ({
    addNotification: (message, level) =>
        dispatch(addNotificationAction(message, level, notificationOptParams)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Graph);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/Graph.jsx